import React, { useState, useEffect } from 'react';
import { Input, Select } from '../ui/Input';
import { PhoneInput } from '../ui/PhoneInput';
import { Button } from '../ui/Button';
import { getProperties } from '../../api/properties';
import { getUnits } from '../../api/units';
import { validatePersonInput } from '../bulkImport/validation';

interface TenantFormProps {
  initialData?: any;
  propertyId?: number | string;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
}

export const TenantForm: React.FC<TenantFormProps> = ({ initialData, propertyId: propPropertyId, onSubmit, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [properties, setProperties] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);

  const [firstName, setFirstName] = useState(initialData?.first_name || '');
  const [lastName, setLastName] = useState(initialData?.last_name || '');
  const [email, setEmail] = useState(initialData?.email || '');
  const [phone, setPhone] = useState(initialData?.phone || '');
  const [propertyId, setPropertyId] = useState<string>(String(propPropertyId || initialData?.property_id || ''));
  const [unitId, setUnitId] = useState<string>(String(initialData?.unit_id || ''));
  const [isPrimary, setIsPrimary] = useState(initialData?.is_primary || false);
  const [acsProvider, setAcsProvider] = useState(initialData?.acs_provider || '');
  const [acsUserId, setAcsUserId] = useState(initialData?.external_id || initialData?.pdk_holder_id || '');

  useEffect(() => { getProperties().then(setProperties).catch(() => {}); }, []);

  useEffect(() => {
    if (propertyId) {
      getUnits({ property_id: propertyId }).then((all: any[]) => {
        // Filter client-side by property UUID since API may return all
        const filtered = all.filter((u: any) => String(u.property_id) === String(propertyId));
        setUnits(filtered.length > 0 ? filtered : all);
      }).catch(() => setUnits([]));
    } else {
      setUnits([]);
    }
  }, [propertyId]);

  const validate = () => {
    const shared = validatePersonInput({ first_name: firstName, last_name: lastName, email });
    const e: Record<string, string> = {
      ...(shared.first_name ? { firstName: shared.first_name } : {}),
      ...(shared.last_name ? { lastName: shared.last_name } : {}),
      ...(shared.email ? { email: shared.email } : {}),
    };
    // Property is only required on the CREATE flow — Edit doesn't touch it.
    if (!initialData && !propertyId) e.propertyId = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      if (initialData) {
        // Edit flow: only identity fields are mutable here. Property / unit /
        // ACS linkage is managed on the People page itself.
        await onSubmit({
          ...initialData,
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
        });
      } else {
        await onSubmit({
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          property_id: propertyId,
          unit_id: unitId || undefined,
          is_primary: isPrimary,
          status: 'active',
          acs_provider: acsProvider || null,
          external_id: acsUserId || null,
          ...(acsProvider === 'pdk' ? { pdk_holder_id: acsUserId || null } : {}),
        });
      }
    } catch (err: any) {
      setErrors({ submit: err?.response?.data?.message || err.message || 'Failed to save person' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="text-lg font-semibold text-gray-900 mb-6">{initialData ? 'Edit' : 'New'} Person</h3>

      {errors.submit && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{errors.submit}</div>}

      <div className="grid grid-cols-2 gap-3">
        <Input label="First Name *" value={firstName} onChange={e => setFirstName(e.target.value)} error={errors.firstName} />
        <Input label="Last Name *" value={lastName} onChange={e => setLastName(e.target.value)} error={errors.lastName} />
      </div>
      <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} error={errors.email} />
      <PhoneInput label="Phone" value={phone} onChange={setPhone} />

      {/* Property / Unit / ACS controls only appear on the CREATE flow.
          For an existing person these are managed on the People page itself
          (Assigned Units + Permissions per Property tables), so the Edit
          modal stays focused on identity fields only. */}
      {!initialData && (
        <>
          {propPropertyId ? (
            <p className="text-sm text-gray-500 mb-4">Property: {properties.find(p => p.uuid === propPropertyId || String(p.id) === String(propPropertyId))?.name || `#${propPropertyId}`}</p>
          ) : (
            <Select label="Property *" value={propertyId} onChange={e => { setPropertyId(e.target.value); setUnitId(''); }}>
              <option value="">Select property...</option>
              {properties.map((p: any) => <option key={p.uuid || p.id} value={p.uuid || p.id}>{p.name}</option>)}
            </Select>
          )}
          {errors.propertyId && <p className="text-red-500 text-xs -mt-3 mb-4">{errors.propertyId}</p>}

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Select label="Unit" value={unitId} onChange={e => setUnitId(e.target.value)}>
                <option value="">No unit</option>
                {units.map((u: any) => {
                  const isCurrent = String(u.uuid || u.id) === String(initialData?.unit_id);
                  const occupied = u.status === 'occupied' && !isCurrent;
                  return <option key={u.uuid || u.id} value={u.uuid || u.id} disabled={occupied}>{u.unit_number || u.name || `Unit #${u.id}`}{occupied ? ' (occupied)' : ''}</option>;
                })}
              </Select>
            </div>
            {propertyId && (
              <Button type="button" size="sm" variant="secondary" onClick={() => {
                const unitNum = prompt('Enter new unit number:');
                if (!unitNum) return;
                const floor = prompt('Floor number (optional):');
                (async () => {
                  try {
                    const { apiCreate } = await import('../../api/apiClient');
                    const created = await apiCreate('units', { property_id: propertyId, unit_number: unitNum, floor: floor || null, status: 'active' });
                    const newId = created.uuid || created.id;
                    setUnits(prev => [...prev, { ...created, uuid: newId, unit_number: unitNum }]);
                    setUnitId(String(newId));
                  } catch (e: any) { alert('Failed to create unit: ' + (e.message || e)); }
                })();
              }}>+ New Unit</Button>
            )}
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
              Primary Contact
            </label>
          </div>
        </>
      )}

      <div className="flex gap-3 justify-end mt-6">
        <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={loading}>{initialData ? 'Update' : 'Create'} Person</Button>
      </div>
    </form>
  );
};
